import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  Code2,
  Users,
  Lightbulb,
  DollarSign,
  ArrowRight,
  MessageCircle
} from 'lucide-react'

interface PartnershipProgram {
  id: string
  titleKey: string
  descriptionKey: string
  icon: React.ElementType
  color: string
  bgColor: string
  commissionKey?: string
}

const partnershipPrograms: PartnershipProgram[] = [
  {
    id: 'reseller',
    titleKey: 'programs.reseller.title',
    descriptionKey: 'programs.reseller.description',
    icon: Users,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    commissionKey: 'programs.reseller.commission'
  },
  {
    id: 'integrator',
    titleKey: 'programs.integrator.title',
    descriptionKey: 'programs.integrator.description',
    icon: Code2,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    commissionKey: 'programs.integrator.commission'
  },
  {
    id: 'technology',
    titleKey: 'programs.technology.title',
    descriptionKey: 'programs.technology.description',
    icon: Lightbulb,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    commissionKey: 'programs.technology.commission'
  }
]

export const PartnersPage: React.FC = () => {
  const { t } = useTranslation('partners')
  
  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="py-32 bg-gradient-to-br from-kylix-primary to-kylix-secondary text-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl font-bold mb-6"
            >
              {t('page.title')}
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-white/90 mb-8 leading-relaxed max-w-3xl mx-auto"
            >
              {t('page.description')}
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/contact">
                <button className="btn-primary bg-white text-kylix-primary hover:bg-gray-100 px-8 py-4 text-lg">
                  <MessageCircle className="w-5 h-5" />
                  {t('page.cta.contact')}
                </button>
              </Link>
              
              <Link to="/">
                <button className="px-8 py-4 text-lg rounded-lg font-semibold bg-transparent border-2 border-white text-white hover:bg-white hover:text-kylix-primary transition-colors inline-flex items-center gap-2">
                  <ArrowRight className="w-5 h-5 rotate-180" />
                  {t('page.cta.back')}
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partnership Programs */}
      <section className="py-24 bg-white">
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {partnershipPrograms.map((program, index) => (
                <motion.div
                  key={program.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative p-8 rounded-2xl border-2 transition-all hover:shadow-lg ${
                    program.id === 'reseller' 
                      ? `${program.bgColor} border-current ${program.color}` 
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {program.id === 'reseller' && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-kylix-secondary text-white px-4 py-1 rounded-full text-sm font-semibold">
                        {t('labels.featured')}
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center">
                    <div className={`w-16 h-16 rounded-full ${program.bgColor} flex items-center justify-center mx-auto mb-6`}>
                      <program.icon className={`w-8 h-8 ${program.color}`} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{t(program.titleKey)}</h3>
                    <p className="text-gray-600 mb-6">{t(program.descriptionKey)}</p>
                    {program.commissionKey && (
                      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold ${program.bgColor} ${program.color} mb-6`}>
                        <DollarSign className="w-4 h-4" />
                        {t(program.commissionKey)}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-16 text-center"
            >
              <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                {t('labels.bottomDescription')}
              </p>
              
              <Link to="/contact">
                <button className="btn-primary text-lg px-8 py-4">
                  <MessageCircle className="w-5 h-5" />
                  {t('labels.contactButton')}
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}